// @givmo/board — putting a consent to the board, and showing exactly whom it fixes as required.
//
// ══ WHY THIS CONFIRMATION EXISTS AT ALL ═════════════════════════════════════════════════════════
//
// Circulating is the moment the required set is FIXED. The backend snapshots the roster verbatim —
// its serving DIRECTORS only (CBG-YR3-ROLES' G3) — and from then on:
//
//   * a seat ADDED afterwards is neither required nor able to sign (it has no snapshot row, so there is
//     no id for a signature of theirs to point at); and
//   * a seat REMOVED afterwards STAYS required, because the required set is a property of the CONSENT.
//     If removal recomputed it, a board could be edited into unanimity by dropping whoever had not
//     signed.
//
// Both edges are load-bearing and neither is undoable. The remedy for a wrong roster is a REVISION,
// which takes a fresh snapshot and costs every signature already given. So the officer sees the list
// before they commit to it, not after.
//
// ⚠ THE LIST BELOW IS A PREVIEW OF THE SEATS AS RECORDED NOW, NOT THE SNAPSHOT ITSELF. The snapshot is
// taken by the backend inside the circulation write, from the roster at that instant — so a seat added
// by a colleague in the seconds between this dialog opening and the officer confirming will be in the
// snapshot and not in this list. The copy says "as your roster stands now" for that reason; presenting a
// client-side read as though it were the record would be the wrong claim about the one thing this
// dialog exists to make visible.
//
// WHO ISN'T ASKED (CBG-YR3-ROLES layer 1, D-CBG-21). The preview lists the directors — the positive
// fact `is_director`, never a negative test on the legacy word (the derivation's G5) — each with their
// facts, and names once whoever on the roster isn't a director, and that everyone can read it.
//
// A consent that APPROVES MINUTES (CBG-RC9) says, before it goes out, what the signatures will do:
// approve those minutes as they are now, named in its text by their fingerprint, and what happens if
// they change before the last signature (the consent is still filed, and approves nothing).
//
// WHAT THE BOARD IS TOLD (CBG-OPS1-CIRC). Circulating emails every director on the snapshot that a
// written consent is waiting for their signature, once the write has committed (a director no account
// has joined gets words that send them to their invitation first), and records for each seat whether
// it went. The dialog says so before the officer presses, describing the mail in this row's own
// approved words (`consent-delivery-words.ts`), never quoting it; the consent's page then says, beside
// each director, what the email did. The email is a notice, never the record: `circulated_at` plus the
// snapshot row is still who the consent went to, and when.
//
// A COMMITTEE'S consent (CBG-YR6-CONSENT) goes to the members who vote on the committee: the backend
// snapshots them (`voting_seats`) where it snapshots the board's directors, so the preview reads the
// committee — its members whose membership stands, recorded as voting, still serving on the board —
// names once those recorded as not voting, and says the committee's words for the rest.

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCommitteeMemberOut,
  type OrgBoardMemberOut,
} from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, Skeleton, toast } from '@givmo/design-system';
import { extractBoardMembers } from '../roster/roster-decode';
import {
  capacityLine,
  joinWords,
  servingDirectors,
  servingNonDirectors,
  termEndedBefore,
} from '../roster/officer-roles';
import { officerRolesWords as roles } from '../roster/officer-roles-words';
import { termNoteWords } from '../roster/term-note-words';
import { displayDate } from '../../lib/display';
import { useBoardOperations } from '../../board-runtime';
import { consentDeliveryWords as circ } from './consent-delivery-words';
import { minutesConsentWords as rc9 } from './minutes-consent-words';
import { minutesSetsWords as several } from './minutes-sets-words';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { committeeSigners, memberFacts } from '../committees/committees-decode';

const byName = (left: OrgBoardMemberOut, right: OrgBoardMemberOut) =>
  left.display_name.localeCompare(right.display_name);

/** Seats the backend will snapshot: the serving directors (G5: the positive fact). */
export function seatsThatWillBeRequired(seats: readonly OrgBoardMemberOut[]): OrgBoardMemberOut[] {
  return servingDirectors(seats).sort(byName);
}

/** Serving seats the consent won't go to: everyone on the board who isn't a director. A seat for
 *  someone who isn't on the board (CBG-YR6-OUTSIDE) is no part of the board's consent, not even as
 *  a reader, so it isn't named. */
export function seatsNotAskedToSign(seats: readonly OrgBoardMemberOut[]): OrgBoardMemberOut[] {
  return servingNonDirectors(seats.filter((seat) => seat.scope !== 'committees')).sort(byName);
}

export interface CirculateDialogProps {
  orgRef: string;
  consentId: string;
  consentTitle: string;
  /**
   * For a consent that approves minutes (CBG-RC9): those minutes in a sentence ("the minutes of the
   * Annual board meeting of September 24, 2026"). Absent for every other consent.
   */
  approvesMinutes?: string | null;
  /**
   * CBG-RC9-MULTI: the consent names two or more sets of one meeting's minutes (`approvesMinutes`
   * names them all), each pinned by its own fingerprint and decided on its own.
   */
  approvesSeveralSets?: boolean;
  /**
   * The consent's own date (`consent_date`): a director whose recorded term end is before it gets the
   * consent page's note here too, by that page's own rule (CBG-YR3-VOTENOTE). None, no note.
   */
  consentDate?: string | null;
  /** CBG-YR6-CONSENT: a committee's consent — its id and name — goes to its voting members. */
  committeeId?: string | null;
  committeeName?: string | null;
  onClose: () => void;
  onCirculated: () => void;
}

export function CirculateDialog({
  orgRef,
  consentId,
  consentTitle,
  approvesMinutes = null,
  approvesSeveralSets = false,
  consentDate = null,
  committeeId = null,
  committeeName = null,
  onClose,
  onCirculated,
}: CirculateDialogProps) {
  const operations = useBoardOperations();
  const ofACommittee = committeeId !== null;
  const rosterQuery = useQuery({
    queryKey: ['board-roster-preview', orgRef],
    enabled: !ofACommittee,
    queryFn: async (): Promise<OrgBoardMemberOut[]> => {
      const { data } = await operations.officerListBoardMembers({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractBoardMembers(data);
    },
  });
  const committeeQuery = useQuery({
    ...operations.officerGetBoardCommitteeOptions({
      path: { org_ref: orgRef, committee_id: committeeId ?? '' },
    }),
    enabled: ofACommittee,
  });
  const previewQuery = ofACommittee ? committeeQuery : rosterQuery;
  const committee = committeeName ?? cm.aCommittee;
  const signers = committeeQuery.data ? committeeSigners(committeeQuery.data) : null;

  const required = seatsThatWillBeRequired(rosterQuery.data ?? []);
  const notAsked = seatsNotAskedToSign(rosterQuery.data ?? []);
  const voting: BoardCommitteeMemberOut[] = signers?.voting ?? [];
  const notVoting: BoardCommitteeMemberOut[] = signers?.notVoting ?? [];
  const willSign = ofACommittee ? voting.length : required.length;

  const circulate = useMutation({
    mutationFn: async () => {
      await operations.officerCirculateBoardWrittenConsent({
        path: { org_ref: orgRef, consent_id: consentId },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(
        ofACommittee ? cm.nowBeforeCommittee(committee) : 'This consent is now before your board.',
      );
      onCirculated();
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !circulate.isPending && onClose()}>
      <Dialog.Content
        aria-label={ofACommittee ? cm.putToTheCommittee : 'Put this consent to the board'}
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="consent-circulate-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {ofACommittee
            ? cm.putTitleToCommittee(consentTitle, committee)
            : `Put “${consentTitle}” to your board`}
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {ofACommittee ? cm.circulateIntro : roles.circulateIntro}
        </p>

        {previewQuery.isPending ? (
          <Skeleton className="h-[64px] w-full rounded-lg" testId="consent-circulate-loading" />
        ) : null}

        {previewQuery.isError ? (
          <ErrorMessage testId="consent-circulate-roster-error">
            We couldn’t read your board members just now, so we can’t show you who this would go to.
          </ErrorMessage>
        ) : null}

        {previewQuery.isSuccess ? (
          <div className="flex flex-col gap-6" data-testid="consent-circulate-roster">
            <span className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
              {ofACommittee ? cm.circulateWho(willSign, committee) : roles.circulateWho(willSign)}
            </span>
            {willSign === 0 ? (
              <span
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="consent-circulate-empty-roster"
              >
                {ofACommittee ? cm.circulateNobody(committee) : roles.circulateNoDirectors}
              </span>
            ) : ofACommittee ? (
              <ul className="flex flex-col gap-4">
                {voting.map((member) => (
                  <li
                    key={member.id}
                    className="flex flex-wrap items-baseline gap-8"
                    data-testid={`consent-circulate-seat-${member.board_member_id}`}
                  >
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {member.display_name}
                    </span>
                    <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                      {memberFacts(member)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="flex flex-col gap-4">
                {required.map((seat) => {
                  // CBG-YR3-VOTENOTE: what the consent's page will say of this seat once it is out,
                  // said before anyone signs. A recorded fact, never a bar: the seat is asked to sign
                  // either way.
                  const ended = termEndedBefore(seat.term_end_on, consentDate);
                  return (
                    <li
                      key={seat.id}
                      className="flex flex-wrap items-baseline gap-8"
                      data-testid={`consent-circulate-seat-${seat.id}`}
                    >
                      <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                        {seat.display_name}
                      </span>
                      <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                        {capacityLine(seat)} · {seat.email_of_record}
                      </span>
                      {ended !== null ? (
                        <span
                          className="basis-full font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                          data-testid={`consent-circulate-seat-term-${seat.id}`}
                        >
                          {termNoteWords.termYouRecordedEnded(displayDate(ended))}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {(ofACommittee ? notVoting.length : notAsked.length) > 0 ? (
              // Named once, so the officer isn't left wondering where they went.
              <span
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                data-testid="consent-circulate-not-asked"
              >
                {ofACommittee
                  ? cm.circulateNotAsked(
                      joinWords(notVoting.map((member) => member.display_name ?? '')),
                      notVoting.length,
                    )
                  : roles.circulateNotAsked(
                      joinWords(notAsked.map((seat) => seat.display_name)),
                      notAsked.length,
                    )}
              </span>
            ) : null}
          </div>
        ) : null}

        {approvesMinutes !== null ? (
          <div
            className="flex flex-col gap-4 rounded-md border border-border-subtle bg-surface-sunken p-12"
            data-testid="consent-circulate-approves-minutes"
          >
            <span className="font-sans text-s13 font-w600 leading-h15 tracking-givmo text-text-strong">
              {approvesSeveralSets
                ? several.circulateLead(approvesMinutes)
                : rc9.circulateLead(approvesMinutes)}
            </span>
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {approvesSeveralSets ? several.circulateIfChanged : rc9.circulateIfChanged}
            </span>
          </div>
        ) : null}

        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid="consent-circulate-email"
        >
          {ofACommittee ? cm.circulateEmails : circ.circulateEmails}
        </p>

        {circulate.isError ? (
          <ErrorMessage testId="consent-circulate-error">
            {isGivmoError(circulate.error)
              ? circulate.error.message
              : ofACommittee
                ? cm.couldntPutToCommittee
                : 'We couldn’t put this to your board. Please try again.'}
          </ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={circulate.isPending}
            onClick={onClose}
            testId="consent-circulate-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={circulate.isPending}
            disabled={willSign === 0 || previewQuery.isPending}
            onClick={() => circulate.mutate()}
            testId="consent-circulate-confirm"
          >
            {ofACommittee ? cm.putToTheCommittee : 'Put it to the board'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
